import { uniqueId } from 'lodash'
import React, { ReactNode, useContext, useEffect, useRef, useState } from 'react'
import Loading from 'shared/components/loaders/dots'
import useAsyncEffect from 'shared/hooks/use-async-effect'
import styled from 'styled-components'
import { BORDER_RADIUS_MEDIUM } from '../../constants'
import { CreationToolsContext } from '../../manager'
import { CreationToolFrame, CreationToolSourceFile } from '../../types'
import CreationToolFileType from '../../types/file-type'
import CreationToolOutputType from '../../types/output-type'
import getSourceFrames from '../../utils/get-source-frames'
import { SourceEditorEdits } from '../source-editor/types'
import Controls from './controls'
import Media from './media'

const Player = styled.div`
    border-radius: ${BORDER_RADIUS_MEDIUM}px;
    position: relative;
`

type Props = {
    className?: string
    children?: ReactNode
    source: CreationToolSourceFile
    outputType: CreationToolOutputType
    edits?: SourceEditorEdits
    forceFrames?: boolean
    overlay?: ReactNode
    onTimeUpdate?: (currentTime: number) => void
}

const requiresFrameFetch = (source: CreationToolSourceFile, edits?: SourceEditorEdits): boolean => {
    // do not parse video frames
    // TODO: update this when we start creating videos on the front-end
    if (CreationToolFileType.isVideo(source.type) || source.frames.length > 0) {
        return false
    }

    // if there are multiple edit frames, we need source frames to sync them
    if (edits?.frames && edits.frames.length > 0) {
        return true
    }

    // if the soure is trimmed and not a video, we need frames so we can trim the GIF
    if (edits?.trim) {
        return true
    }

    // if the source is cropped and not a video, we need frames so we can crop the GIF
    if (edits?.crop) {
        return true
    }

    return false
}

const CreationToolsSourcePlayer = ({
    className,
    children,
    source,
    outputType,
    edits,
    forceFrames = false,
    overlay,
    onTimeUpdate,
}: Props) => {
    const { currentAudioId, setCurrentAudioId } = useContext(CreationToolsContext)
    const playerIdRef = useRef<string>(uniqueId('sourceplayer'))
    const [sourceFrames, setSourceFrames] = useState<CreationToolFrame[]>(source.frames)
    const [isPlaying, setIsPlaying] = useState<boolean>(true)
    const [isMuted, setIsMuted] = useState<boolean>(true)
    const [isFetchingFrames, setIsFetchingFrames] = useState<boolean>(false)
    const showControls = outputType === CreationToolOutputType.Video

    useEffect(() => {
        // if an audio source id is set and it's not this player, mute it
        if (currentAudioId !== playerIdRef.current) {
            setIsMuted(true)
        }
    }, [currentAudioId])

    useEffect(() => {
        if (!isMuted) {
            setCurrentAudioId(playerIdRef.current)
        }
    }, [isMuted, setCurrentAudioId])

    useAsyncEffect(async () => {
        if (sourceFrames.length > 0) return

        if (forceFrames || requiresFrameFetch(source, edits)) {
            setIsFetchingFrames(true)
            const frames = await getSourceFrames({ sourceFile: source })
            setSourceFrames(frames)
            setIsFetchingFrames(false)
        }
    }, [source, edits, forceFrames, setSourceFrames])

    return (
        <Player className={className}>
            <Media
                {...{
                    edits,
                    overlay,
                    isMuted,
                    isPlaying,
                    source: { ...source, frames: sourceFrames },
                    onTimeUpdate,
                }}
            >
                {isFetchingFrames && <Loading isAbsolute />}
            </Media>
            {children}
            {showControls && (
                <Controls
                    {...{
                        isMuted,
                        isPlaying,
                        onMuteToggle: setIsMuted,
                        onPlayToggle: setIsPlaying,
                    }}
                />
            )}
        </Player>
    )
}

export default CreationToolsSourcePlayer
